import { useAtomValue } from "jotai";
import Image from "next/image";
import { useMemo, useState } from "react";

import { VariantFinishEnum } from "@sorcery/db";

import { ModalTypeEnum, useModal } from "~/providers/ModalProvider";
import { deckJotai, decklistJotai, maybeboardJotai, sideboardJotai } from "~/state/deckJotai";
import type { CardType, VariantType } from "~/types/card";
import { DEFAULT_CARD_HEIGHT, DEFAULT_CARD_WIDTH } from "~/utils/card";
import { DeckTicker } from "../deck/DeckTicker";
import { CardFlipper } from "./CardFlipper";

export const CardPreviewImage = ({ card }: { card: CardType }) => {
  const { openModal } = useModal();
  const deck = useAtomValue(deckJotai);
  const decklist = useAtomValue(decklistJotai);
  const sideboard = useAtomValue(sideboardJotai);
  const maybeboard = useAtomValue(maybeboardJotai);

  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const variant = card.variants[0]!;
  const hasReverse = !!card.reverse;
  const [isReversed, setIsReversed] = useState(false);

  const toggleVariant = () => {
    setIsReversed(!isReversed);
  };

  const displayedVariant =
    isReversed && hasReverse
      ? // the no reverse on a reverse issue
        (variant.reverse as unknown as VariantType)
      : variant;

  /**
   * deck management
   */

  const inDeck = useMemo(() => !!decklist?.find((c) => c.cardId === card.id), [card.id, decklist]);

  const inSideboard = useMemo(
    () => !!sideboard?.find((c) => c.cardId === card.id),
    [card.id, sideboard],
  );

  const inMaybeboard = useMemo(
    () => !!maybeboard?.find((c) => c.cardId === card.id),
    [card.id, maybeboard],
  );

  const shading = useMemo(() => {
    if (inDeck) {
      return "!shadow-[0_0_16px_rgba(255,123,0,0.9)] hover:outline-primary";
    }
    if (inSideboard || inMaybeboard) {
      return "!shadow-[0_0_16px_rgba(141,131,242,0.9)] hover:outline-info";
    }
  }, [inDeck, inSideboard, inMaybeboard]);

  return (
    <>
      <div className={"flex flex-row items-center justify-center duration-700 animate-in fade-in"}>
        <div
          className={`relative cursor-pointer rounded-[20px] outline outline-1 outline-transparent transition-all animate-in
            fade-in hover:!shadow-[0_0_16px_rgba(255,255,255,0.75)] hover:!outline-white
            ${shading}`}
          // constrain container size for library previews
          // todo check against 1.5 for spacing
          style={{
            height: DEFAULT_CARD_HEIGHT / 1.55,
            width: DEFAULT_CARD_WIDTH / 1.55,
          }}
          key={displayedVariant.id}
          onClick={() => {
            if (deck) {
              return openModal(ModalTypeEnum.DECKGALLERY, {
                card,
              });
            }
            return openModal(ModalTypeEnum.CARDGALLERY, { card });
          }}
        >
          <Image
            height={DEFAULT_CARD_HEIGHT}
            width={DEFAULT_CARD_WIDTH}
            alt={card.name}
            src={displayedVariant.src}
          />
          {hasReverse && <CardFlipper onFlip={toggleVariant} />}

          {variant.finish === VariantFinishEnum.Foil && (
            <div
              className={`absolute left-0 top-0 h-full w-full rounded-[20px] opacity-30 ${shading}`}
              style={{
                background:
                  "repeating-linear-gradient(-45deg,rgba(233, 238, 82, 0.5),rgba(231, 60, 126, 0.5),rgba(35, 166, 213, 0.5),rgba(35, 213, 171, 0.5))",
                backgroundSize: "400% 400%",
                backgroundPosition: "0 0",
                animation: "sparkle 5s ease-in-out infinite",
              }}
            />
          )}
        </div>
      </div>

      {/* quantity update footer */}
      {!!deck && <DeckTicker card={card} />}
    </>
  );
};
